/**
 * Connection Quality Indicator - Shows real-time bandwidth usage and latency
 */

import React, { useState, useEffect } from 'react';
import { SignalIcon, SignalSlashIcon } from '@heroicons/react/24/outline';

interface ConnectionQualityProps {
  sessionId: string;
  bytesUploaded: number;
  bytesDownloaded: number;
  startedAt?: Date;
}

interface SpeedMetrics {
  uploadSpeed: number;   // KB/s
  downloadSpeed: number; // KB/s
  avgUploadSpeed: number;
  avgDownloadSpeed: number;
}

const ConnectionQualityIndicator: React.FC<ConnectionQualityProps> = ({
  sessionId,
  bytesUploaded,
  bytesDownloaded,
  startedAt,
}) => {
  const [, setPrevBytes] = useState({ uploaded: bytesUploaded, downloaded: bytesDownloaded });
  const [speed, setSpeed] = useState<SpeedMetrics>({
    uploadSpeed: 0,
    downloadSpeed: 0,
    avgUploadSpeed: 0,
    avgDownloadSpeed: 0,
  });
  const [quality, setQuality] = useState<'excellent' | 'good' | 'fair' | 'poor'>('good');

  // Calculate speed every second
  useEffect(() => {
    const interval = setInterval(() => {
      setPrevBytes((prev) => {
        const uploadedDiff = bytesUploaded - prev.uploaded;
        const downloadedDiff = bytesDownloaded - prev.downloaded;

        // Calculate instantaneous speed (KB/s)
        const uploadSpeed = uploadedDiff / 1024;
        const downloadSpeed = downloadedDiff / 1024;

        // Calculate average speed since session start
        let avgUploadSpeed = 0;
        let avgDownloadSpeed = 0;

        if (startedAt) {
          const durationSeconds = (new Date().getTime() - startedAt.getTime()) / 1000;
          if (durationSeconds > 0) {
            avgUploadSpeed = (bytesUploaded / 1024) / durationSeconds;
            avgDownloadSpeed = (bytesDownloaded / 1024) / durationSeconds;
          }
        }

        setSpeed({
          uploadSpeed,
          downloadSpeed,
          avgUploadSpeed,
          avgDownloadSpeed,
        });

        // Determine connection quality based on total speed
        const totalSpeed = uploadSpeed + downloadSpeed;
        if (totalSpeed > 1000) {
          setQuality('excellent');
        } else if (totalSpeed > 100) {
          setQuality('good');
        } else if (totalSpeed > 10) {
          setQuality('fair');
        } else {
          setQuality('poor');
        }

        return { uploaded: bytesUploaded, downloaded: bytesDownloaded };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [bytesUploaded, bytesDownloaded, startedAt]);

  const formatSpeed = (kbps: number): string => {
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(1)} MB/s`;
    }
    return `${kbps.toFixed(1)} KB/s`;
  };

  const getQualityColor = () => {
    switch (quality) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-primary-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
    }
  };

  const getQualityBgColor = () => {
    switch (quality) {
      case 'excellent': return 'bg-green-50';
      case 'good': return 'bg-blue-50';
      case 'fair': return 'bg-yellow-50';
      case 'poor': return 'bg-red-50';
    }
  };

  const getSignalBars = () => {
    switch (quality) {
      case 'excellent': return 4;
      case 'good': return 3;
      case 'fair': return 2;
      case 'poor': return 1;
    }
  };

  const totalSpeed = speed.uploadSpeed + speed.downloadSpeed;
  const isActive = totalSpeed > 0.1;

  return (
    <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-md ${getQualityBgColor()}`}>
      {/* Signal Icon */}
      <div className={getQualityColor()}>
        {isActive ? (
          <SignalIcon className="h-4 w-4" />
        ) : (
          <SignalSlashIcon className="h-4 w-4" />
        )}
      </div>

      {/* Speed Display */}
      <div className="text-xs space-y-0.5">
        {isActive ? (
          <>
            <div className={`font-medium ${getQualityColor()}`}>
              ↓ {formatSpeed(speed.downloadSpeed)} ↑ {formatSpeed(speed.uploadSpeed)}
            </div>
            {startedAt && (speed.avgDownloadSpeed > 0.1 || speed.avgUploadSpeed > 0.1) && (
              <div className="text-gray-500 text-[10px]">
                Avg: ↓ {formatSpeed(speed.avgDownloadSpeed)} ↑ {formatSpeed(speed.avgUploadSpeed)}
              </div>
            )}
          </>
        ) : (
          <div className="text-gray-500">Idle</div>
        )}
      </div>

      {/* Signal Strength Bars */}
      <div className="flex items-end space-x-0.5 h-3">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 ${
              bar <= getSignalBars()
                ? getQualityColor().replace('text-', 'bg-')
                : 'bg-gray-300'
            }`}
            style={{ height: `${bar * 25}%` }}
          />
        ))}
      </div>
    </div>
  );
};

export default ConnectionQualityIndicator;
